import { isFulfilled } from '@reduxjs/toolkit';
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from 'constants/AppConstants';
import { useLoader } from 'contexts/LoaderContext';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { isAgent, isLandlord, isTenant } from 'services/TenantSubmitHandler';
import { StoreDispatch, useAppSelector } from 'store/configureStore';
import { TenantPetsDocument } from 'store/tenant/types';
import { isQldRegion } from 'utils/petFormHelpers';
import { isDocGenSupportedCharacters } from 'utils/validationMap';
import { addUploadedDocument, markPetDocumentsLoaded, removeUploadedDocument } from '../../store/formTemplate/formTemplateSlice';
import { deleteDocument, uploadDocument } from '../../store/formTemplate/formTemplateThunks';
import ErrorMessage from './ErrorMessage';
import Icon from './Icon/Icon';
import InputField from './InputField';

interface UploadedFile {
    id: string;
    file: File;
    description: string;
    pet_type?: string;
    property_id?: string;
    
}

export const formatFileSize = (size: number | string): string => {
    // If it's a string that already contains units, return as-is
    if (typeof size === 'string' && /\s*(KB|MB|GB|bytes)$/i.test(size.trim())) {
        return size.trim();
    }
    
    // Convert string to number if needed
    const numericSize = typeof size === 'string' ? parseFloat(size) : size;
    
    // If conversion failed or invalid, return original
    if (isNaN(numericSize)) {
        return String(size);
    }
    
    if (numericSize >= 1024 ** 3) return (numericSize / 1024 ** 3).toFixed(2) + ' GB';
    if (numericSize >= 1024 ** 2) return (numericSize / 1024 ** 2).toFixed(2) + ' MB';
    if (numericSize >= 1024) return (numericSize / 1024).toFixed(2) + ' KB';
    return numericSize + ' bytes';
};

interface UploadDocumentProps {
    pet_type?: string;
    property_id?: string;
    pet_index?: number;
    handleUploadPetDocuments?: (files: any) => void;
    petDocuments?: TenantPetsDocument[];
    pet_number?: number;
    petPosition?: number;
    onValidationChange?: (hasErrors: boolean) => void;
}

const UploadDocument: React.FC<UploadDocumentProps> = ({ pet_type, property_id, pet_index, pet_number, handleUploadPetDocuments, petDocuments, petPosition, onValidationChange }) => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [descriptionErrors, setDescriptionErrors] = useState<Record<string, string>>({});
    const propertyId = useAppSelector(state => state.Tenant.propertyId);
    const uploadedDocumentsList = useAppSelector((state) => state.formTemplate.uploadedDocuments);
    const loadedPetIds = useAppSelector((state) => state.formTemplate.loadedPetIds);
        // Get register from form context (optional, may not always be in a form)
    const formContext = useFormContext();
    const control = formContext?.control;
    const formErrors = formContext?.formState?.errors;
    const setValue = formContext?.setValue;
    const trigger = formContext?.trigger;
        const dispatch = useDispatch<StoreDispatch>();
    const { showLoader, hideLoader } = useLoader();
    const stageOnePetsData = useAppSelector((state) => state.Tenant.tenantPets);
    
    // Track if we've already loaded initial documents from server for this pet
    // Use component instance ID based on initial mount conditions
    const hasLoadedInitialDocs = React.useRef<boolean>(false);

    const acceptedTypes = React.useMemo(
        () => Object.values(ACCEPTED_FILE_TYPES) as string[],
        []
    );

    // Track validation errors from React Hook Form and notify parent
    useEffect(() => {
        if (!formErrors || !onValidationChange) return;
        
        // Check if any description field has errors
        const hasErrors = files.some((file, index) => {
            const fieldName = `desc-${file.id}-${index}`;
            return !!formErrors[fieldName];
        });
        
        onValidationChange(hasErrors);
    }, [formErrors, files, onValidationChange]);


    const validateFile = useCallback(
        (uploadedFile: File): string | null => {
            if (!acceptedTypes.includes(uploadedFile.type)) {
                return `Invalid file type. Please upload ${Object.keys(
                    ACCEPTED_FILE_TYPES
                ).join(", ").toUpperCase()}.`;
            }

            if (uploadedFile.size > MAX_FILE_SIZE) {
                return `File size exceeds the ${MAX_FILE_SIZE / (1024 * 1024)}MB limit.`;
            }

            return null;
        },
        [acceptedTypes]
    );

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
        const uploadedFile = event.target.files[0];
        const validationError = validateFile(uploadedFile);
        if (validationError) {
            setError(validationError);
            event.target.value = ""; // Reset input
            return;
        }

        const formData = new FormData();
        formData.append("file", uploadedFile);
        if (pet_type) formData.append("pet_type", pet_type);
        if(propertyId) formData.append("property_id", propertyId);
        if(petPosition !== undefined) formData.append("pet_number", (petPosition).toString());
        
        
        showLoader();
        try {
            const result = await dispatch(uploadDocument({ formData }));
            if (isFulfilled(result)) {
                const { document_id, original_filename, file_size, pet_number, description } = result.payload.data;
                setFiles((prev) => [
                    ...prev,
                    {
                        id: document_id,
                        file: {
                            name: original_filename,
                            size: file_size || 0,
                        } as File,
                        description: "",
                        pet_type,
                        property_id,
                    },
                ]);
                // Dispatch to Redux store
                dispatch(addUploadedDocument({
                    document_id: document_id,
                    document_description: description,
                    pet_type: pet_type,
                    pet_index: pet_number,
                    original_filename: original_filename,
                    document_name: original_filename,
                    file_size: file_size,
                    mime_type: uploadedFile.type,
                    property_id: propertyId,
                    unique_key: petPosition
                }));
                setError(null); // Clear error on success
            } else {
                setError(
                    typeof result?.payload === 'object' && result?.payload !== null && 'message' in result.payload
                        ? (result.payload as { message?: string }).message || "Failed to upload support documents. Try different file or try again later."
                        : "Failed to upload support documents. Try different file or try again later."
                );
            }
            
        } catch (apiError: any) {
            setError(apiError?.message || "Failed to upload support documents.");
        } finally {
            hideLoader();
            event.target.value = ""; // Reset input
        }
    };

    useEffect(() => {
        handleUploadPetDocuments?.(files);
    }, [files, handleUploadPetDocuments]);

    const removeFile = async (id: string) => {
        if(id === "") return;
        showLoader();
        try {
            const response = await dispatch(deleteDocument({ documentId: id }));
            if (isFulfilled(response)) {
                setFiles((prev) => prev.filter((f) => f.id !== id));
                dispatch(removeUploadedDocument(id)); // <-- remove from Redux store
            }
        } catch (error) {
            setError("Failed to remove document.");
        } finally {
            hideLoader();
        }
    };

    const handleDescriptionChange = (id: string, value: string) => {
        setFiles((prev) =>
            prev.map((f) => (f.id === id ? { ...f, description: value } : f))
        );

        dispatch(addUploadedDocument({
            document_id: id,
            document_description: value
        }));
        
        // Validate the description
        let validationError: string | null = null;
        
        // Check maximum length
        if (value.trim().length > 50) {
            validationError = 'Description must not exceed 50 characters';
        } else {
            // Check for supported characters
            const charValidation = isDocGenSupportedCharacters(value);
            if (charValidation !== true) {
                validationError = charValidation;
            }
        }
        
        if (validationError === null) {
            // Clear error if validation passes
            setDescriptionErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[id];
                const hasErrors = Object.keys(newErrors).length > 0;
                onValidationChange?.(hasErrors);
                return newErrors;
            });
        } else {
            // Set error if validation fails
            setDescriptionErrors(prev => {
                const newErrors = {
                    ...prev,
                    [id]: validationError as string
                };
                onValidationChange?.(true);
                return newErrors;
            });
        }
    };

    /**
     * Load initial documents from server data (stageOnePetsData) on first render ONLY
     * This runs ONCE per component instance when first mounted
     * After this, the Redux-based useEffect handles all document display
     * 
     * CRITICAL: Only loads if documents for this pet_type + petPosition are NOT already in Redux
     * AND the pet actually exists in stageOnePetsData with matching pet_number
     * AND the pet ID hasn't been loaded before (prevents loading deleted pets' documents)
     * This prevents re-loading stale data when positions shift or when adding new pets
     */
    useEffect(() => {
        // Skip if we've already loaded initial documents for THIS component instance
        if (hasLoadedInitialDocs.current) {
            return;
        }
        
        // Check if documents already exist in Redux for this pet
        // If they do, skip loading from stageOnePetsData (prevents stale data)
        if (pet_type && petPosition !== undefined) {
            const existingDocs = uploadedDocumentsList?.filter(
                doc => doc.pet_type === pet_type && doc.pet_index === petPosition
            );
            if (existingDocs && existingDocs.length > 0) {
                // Documents already in Redux, mark as loaded and skip
                hasLoadedInitialDocs.current = true;
                return;
            }
        }
        
        if (
            stageOnePetsData &&
            stageOnePetsData.length > 0 &&
            typeof pet_index === 'number' &&
            pet_type &&
            petPosition !== undefined
        ) {
            // Find the pet data by matching pet_type and the current position
            // For initial load, find by pet_type and animal position (petPosition)
            const sameTypePets = stageOnePetsData.filter(pet => pet.pet_type === pet_type);
            const petData = sameTypePets[petPosition - 1]; // petPosition is 1-based, array is 0-based
            
            // CRITICAL: Check if this pet's documents have already been loaded before
            // This prevents loading deleted pets' documents when a new pet is added at the same position
            if (petData && petData.id && loadedPetIds.includes(petData.id)) {
                
                hasLoadedInitialDocs.current = true;
                return;
            }
            
            // Only load if documents exist
            if (petData && Array.isArray(petData.support_documents) && petData.support_documents.length > 0) {
                
                const mappedFiles = petData.support_documents.map((doc: any) => ({
                    id: doc.id,
                    file: {
                        name: doc.file_name,
                        size: doc.file_size_bytes || doc.file_size || 0,
                        type: doc.mime_type,
                    } as File,
                    description: doc.description_of_attachment || "",
                    pet_type: doc.pet_type,
                    property_id: doc.property_id,
                }));
                
                // Add to Redux store (which will trigger the other useEffect to display them)
                mappedFiles.forEach(fileList => {
                    dispatch(addUploadedDocument({
                        document_id: fileList.id,
                        document_description: fileList?.description || "",
                        pet_type: pet_type,
                        pet_index: petPosition,
                        original_filename: fileList?.file.name,
                        document_name: fileList?.file.name,
                        file_size: String(fileList.file?.size),
                        mime_type: fileList.file?.type                       
                    }));
                });
                
                // Mark this pet's documents as loaded to prevent re-loading
                if (petData.id) {
                    dispatch(markPetDocumentsLoaded(petData.id));
                }
            }
            
            // Mark as loaded for THIS component instance
            // Important: Mark even if no documents found (prevents retry)
            hasLoadedInitialDocs.current = true;
        }
    }, [stageOnePetsData, pet_type, petPosition, uploadedDocumentsList, dispatch, pet_index, loadedPetIds]);

    /**
     * REMOVED: This useEffect was causing conflicts with Redux sync
     * It would overwrite files with stale petDocuments prop data after deletions
     * Now we rely solely on Redux (uploadedDocumentsList) as the single source of truth
     * 
     * Previous code (REMOVED):
     * useEffect(() => {
     *   if (petDocuments && petDocuments.length > 0) {
     *     setFiles(mappedFiles); // This would restore deleted documents!
     *   }
     * }, [petDocuments, pet_type, pet_index]);
     */

    /**
     * Load documents from Redux store based on pet_type and petPosition
     * This ensures documents are displayed correctly after reindexing
     * CRITICAL: This effect must run even when uploadedDocumentsList is empty to clear removed documents
     */
    useEffect(() => {
        // Only proceed if we have the required identifiers
        if (pet_type && petPosition !== undefined) {
            // Filter documents for this specific pet (type + position)
            const relevantDocs = uploadedDocumentsList?.filter(
                doc => doc.pet_type === pet_type && doc.pet_index === petPosition
            ) || [];

            if (relevantDocs.length > 0) {
                const mappedFiles = relevantDocs.map(doc => ({
                    id: doc.document_id,
                    file: {
                        name: doc.original_filename || doc.document_name || '',
                        size: typeof doc.file_size === 'string' ? parseInt(doc.file_size) : (doc.file_size_bytes || 0),
                        type: doc.mime_type || '',
                    } as File,
                    description: doc.document_description || '',
                    pet_type: doc.pet_type,
                    property_id: doc.property_id,
                }));
                
                // Only update if there's a change to avoid infinite loops
                setFiles(prevFiles => {
                    const prevIds = prevFiles.map(f => f.id).sort().join(',');
                    const newIds = mappedFiles.map(f => f.id).sort().join(',');
                    if (prevIds !== newIds) {
                        // Set values in React Hook Form when files change
                        if (setValue && trigger) {
                            mappedFiles.forEach((file, idx) => {
                                const fieldName = `desc-${file.id}-${idx}`;
                                setValue(fieldName, file.description || '', { shouldValidate: false });
                                // Trigger validation after setting the value
                                setTimeout(() => trigger(fieldName), 0);
                            });
                        }
                        return mappedFiles;
                    }
                    return prevFiles;
                });
            } else {
                // No documents for this pet - clear the list
                // This handles both: when a document is removed AND when uploadedDocumentsList is empty
                setFiles(prevFiles => prevFiles.length > 0 ? [] : prevFiles);
            }
        }
    }, [uploadedDocumentsList, pet_type, petPosition, setValue, trigger]);

    
    return (
        <div className="d-flex flex-column gap-10px">
            {/* Extracted ternary logic into a variable for clarity */}
            {(() => {
                let supportDocText: React.ReactNode = null;
                if (isTenant() || window.location.pathname.includes("pet-request")) {
                    supportDocText = (
                        <>
                            <strong className='d-block'>{isQldRegion() ? "5" : "4"}. Supporting Documents (Optional)</strong>
                            <div className='fs-16px fw-400'>Upload files such as a photo of your pet, photo of enclosure, medical exemption from microchipping.</div>
                        </>
                    );
                } else if ((isAgent() || isLandlord()) && files.length > 0) {
                    supportDocText = (
                        <>
                            <strong className='d-block'>{isQldRegion() ? "5" : "4"}.Supporting Documents (Optional)</strong> e.g. photo of pet, photo
                            <div className='fs-16px fw-400'>Upload files such as a photo of your pet, photo of enclosure, medical exemption from microchipping.</div>
                        </>
                    );
                } 
                return (
                    <div className="fs-16px fw-normal text-black d-flex flex-column gap-10px">
                        {supportDocText}
                    </div>
                );
            })()}
            {files.length > 0 && (
                <div className="d-flex flex-column gap-20px ">
                    {files.map((item, index) => (
                        <React.Fragment key={item.id}>
                            <div className="d-flex flex-column gap-10px border-1px border border-grey-3 radius-5px p-10px">
                                <div className="d-flex gap-20px align-items-center justify-content-between">
                                    <div className="d-flex gap-8px align-items-center">
                                        <Icon
                                            name="download"
                                            className="min-w-24px"
                                        />
                                        <div className="fs-16px fw-bold text-black d-flex gap-2px align-items-center">
                                            <div className="text-truncate max-w-62px max-w-sm-150px max-w-md-full">
                                                {item.file.name}
                                            </div>{' '}
                                            <div className="text-nowrap">
                                                (
                                                {formatFileSize(item.file.size)}
                                                )
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-theme bg-white text-grey-2 px-0 h-auto"
                                        onClick={() => removeFile(item.id)}
                                    >
                                        <Icon
                                            name="remove"
                                            color="grey-2"
                                            className="min-w-24px"
                                        />{' '}
                                        <div className="d-none d-md-block">
                                            Remove
                                        </div>
                                    </button>
                                </div>
                                
                                <div className={` ${(isAgent() || isLandlord()) && item.description.length > 0 ? 'd-none' : ''}`}>
                                    {control ? (
                                        <Controller
                                            name={`desc-${item.id}-${index}`}
                                            control={control}
                                            defaultValue={item.description || ''}
                                            rules={{
                                                maxLength: {
                                                    value: 50,
                                                    message: "Description must not exceed 50 characters"
                                                },
                                                validate: {
                                                    supportedChars: (value: string) => {
                                                        if (!value || value.trim().length === 0) return true;
                                                        const result = isDocGenSupportedCharacters(value);
                                                        return result;
                                                    }
                                                }
                                            }}
                                            render={({ field }) => (
                                                <InputField
                                                    id={`desc-${item.id}-${index}`}
                                                    placeholder="File description (optional)"
                                                    variant="editable"
                                                    as="input"
                                                    value={field.value}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        const value = e.target.value;
                                                        field.onChange(value);
                                                        handleDescriptionChange(item.id, value);
                                                        // Trigger validation and check errors immediately
                                                        if (trigger) {
                                                            trigger(`desc-${item.id}-${index}`).then(() => {
                                                                // After validation completes, check for errors
                                                                const hasErrors = files.some((f, idx) => {
                                                                    const fieldName = `desc-${f.id}-${idx}`;
                                                                    return !!formErrors?.[fieldName];
                                                                });
                                                                onValidationChange?.(hasErrors);
                                                            });
                                                        }
                                                    }}
                                                    error={formErrors?.[`desc-${item.id}-${index}`]?.message as string || descriptionErrors[item.id]}
                                                />
                                            )}
                                        />
                                    ) : (
                                        <InputField
                                            id={`desc-${item.id}-${index}`}
                                            placeholder="File description (optional)"
                                            variant="editable"
                                            as="input"
                                            value={item.description}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleDescriptionChange(item.id, e.target.value)
                                            }
                                            error={descriptionErrors[item.id]}
                                        />
                                    )}
                                </div>
                            </div>
                            {/* {index !== files.length - 1 && (
                                <div className="bg-lines min-h-1px"></div>
                            )} */}
                        </React.Fragment>
                    ))}
                </div>
            )}

            {
                (isTenant() || window.location.pathname.includes("pet-request")) && (
                    <div className={`mt-10px ${isAgent() || isLandlord() ? 'd-none' : '' }`}>
                        <label className="h-md-50px border-1px border-dashed cursor-pointer border-grey-1 radius-5px d-flex flex-column flex-md-row gap-8px align-items-center justify-content-center text-center p-20px fs-16px fw-bold">
                            <div className='d-flex gap-8px align-items-center justify-content-center text-center text-grey-1'><Icon name="upload" /> Upload documents</div> 
                            <span className='fw-normal fs-14px text-grey-1'>(Accepted file types: PDF, JPEG, JPG, PNG. Size limit: 5MB)</span>
                            <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.pdf"
                                
                                hidden
                                onChange={handleFileUpload}
                            />
                        </label>
                    </div>
                )   
            }
            
            {error && (
                <ErrorMessage errorMessage={error} />
            )}
        </div>
    );
};

export default UploadDocument;
